import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  GithubAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { auth } from '../config/firebase';
import userService from './userServices';

// Initialize providers
const googleProvider = new GoogleAuthProvider();
const githubProvider = new GithubAuthProvider();
const microsoftProvider = new OAuthProvider('microsoft.com');
const appleProvider = new OAuthProvider('apple.com');

// Google Sign-in
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    
    // Call backend to create/update user
    const response = await userService.firebaseAuth({
      firebaseToken: await user.getIdToken(),
      oauthProvider: 'google',
      userInfo: {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
        providerData: user.providerData
      }
    });
    
    return { 
      success: response.retCode === 0, 
      isFirstTime: response.isFirstTime || false,
      user 
    };
  } catch (error) {
    console.error('Google sign-in error:', error);
    throw error;
  }
};

// GitHub Sign-in
export const signInWithGithub = async () => {
  try {
    const result = await signInWithPopup(auth, githubProvider);
    const user = result.user;
    
    // Call backend to create/update user
    const response = await userService.firebaseAuth({
      firebaseToken: await user.getIdToken(),
      oauthProvider: 'github',
      userInfo: {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
        providerData: user.providerData
      }
    });
    
    return { 
      success: response.retCode === 0, 
      isFirstTime: response.isFirstTime || false,
      user 
    };
  } catch (error) {
    console.error('GitHub sign-in error:', error);
    throw error;
  }
};

// Microsoft Sign-in
export const signInWithMicrosoft = async () => {
  try {
    const result = await signInWithPopup(auth, microsoftProvider);
    const user = result.user;
    
    // Call backend to create/update user
    const response = await userService.firebaseAuth({
      firebaseToken: await user.getIdToken(),
      oauthProvider: 'microsoft',
      userInfo: {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
        providerData: user.providerData
      }
    });
    
    return { 
      success: response.retCode === 0, 
      isFirstTime: response.isFirstTime || false,
      user 
    };
  } catch (error) {
    console.error('Microsoft sign-in error:', error);
    throw error;
  }
};

// Apple Sign-in
export const signInWithApple = async () => {
  try {
    const result = await signInWithPopup(auth, appleProvider);
    const user = result.user;
    
    // Call backend to create/update user
    const response = await userService.firebaseAuth({
      firebaseToken: await user.getIdToken(),
      oauthProvider: 'apple',
      userInfo: {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
        providerData: user.providerData
      }
    });
    
    return { 
      success: response.retCode === 0, 
      isFirstTime: response.isFirstTime || false,
      user 
    };
  } catch (error) {
    console.error('Apple sign-in error:', error);
    throw error;
  }
};

// Email/Password Sign-in
export const signInWithEmail = async (email, password) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const user = result.user;
    
    // Call backend to create/update user
    const response = await userService.firebaseAuth({
      firebaseToken: await user.getIdToken(),
      oauthProvider: 'email',
      userInfo: {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
        providerData: user.providerData
      }
    });
    
    return { 
      success: response.retCode === 0, 
      isFirstTime: response.isFirstTime || false,
      user 
    };
  } catch (error) {
    console.error('Email sign-in error:', error);
    // Map Firebase error codes to user-friendly messages
    let errorMessage = 'Sign in failed. Please try again.';
    if (error.code === 'auth/user-not-found') {
      errorMessage = 'No account found with this email. Please sign up first.';
    } else if (error.code === 'auth/wrong-password') {
      errorMessage = 'Incorrect password. Please try again.';
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address.';
    } else if (error.code === 'auth/user-disabled') {
      errorMessage = 'This account has been disabled.';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Too many failed attempts. Please try again later.';
    } else if (error.code === 'auth/invalid-credential') {
      errorMessage = 'Invalid email or password. Please check your credentials.';
    }
    return { success: false, error: errorMessage };
  }
};

// Email/Password Sign-up
export const signUpWithEmail = async (email, password, displayName) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const user = result.user;
    
    // Update display name if provided
    if (displayName) {
      await updateProfile(user, {
        displayName: displayName
      });
      await user.reload();
    }
    
    // Call backend to create/update user
    const response = await userService.firebaseAuth({
      firebaseToken: await user.getIdToken(),
      oauthProvider: 'email',
      userInfo: {
        uid: user.uid,
        email: user.email,
        displayName: displayName || user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
        providerData: user.providerData
      }
    });
    
    return { 
      success: response.retCode === 0, 
      isFirstTime: true,
      user 
    };
  } catch (error) {
    console.error('Email sign-up error:', error);
    let errorMessage = 'Registration failed. Please try again.';
    if (error.code === 'auth/email-already-in-use') {
      errorMessage = 'An account with this email already exists. Please sign in instead.';
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address.';
    } else if (error.code === 'auth/weak-password') {
      errorMessage = 'Password is too weak. Please use at least 6 characters.';
    } else if (error.code === 'auth/operation-not-allowed') {
      errorMessage = 'Email/password registration is not enabled.';
    }
    return { success: false, error: errorMessage };
  }
};

// Sign out
export const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Sign out error:', error);
    throw error;
  }
};

// Auth state listener
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// Save user to Firestore (for email users)
export const saveUserToFirestore = async (user) => {
  try {
    // This is primarily for email/password users
    // OAuth2 users get their data from the provider
    const userData = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      emailVerified: user.emailVerified,
      oauthProvider: 'email',
      createdAt: new Date(),
      lastSignIn: new Date()
    };
    
    // You can add Firestore logic here if needed
    return userData;
  } catch (error) {
    console.error('Error saving user to Firestore:', error);
    throw error;
  }
};
